import { cn } from '@nextui-org/react'
import { Suspense, useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { LoadingState } from '~/components/common/LoadingSpinner'
import {
  SegmentControls,
  SegmentControlsContent,
  SegmentControlsList,
  SegmentControlsTrigger
} from '~/components/common/SegmentControls'
import { Typography } from '~/components/common/Typography'
import { TaskStatuses } from '~/constants/task-status'
import {
  DATAWEAVE_COMMANDS,
  DIFF_BEARING_STATUSES,
  getTaskTabProfile,
  getVisibleTabs,
  MIGRATION_DIFF_STATUSES
} from '~/task/taskTabs'
import type { TabContentFlags, ViewType } from '~/task/taskTabs'
import { getTaskRequest, useTask } from '~/contexts/TaskContext'
import { useConversationUuid } from '~/hooks'
import { getCodeDeltaContent, getCodeReviewBriefContent } from '~/hooks/task/useCodeDeltaContent'
import { useCodeEditorVisibility } from '~/hooks/task/useCodeEditorVisibility'
import { useTaskBlock } from '~/hooks/task/useTaskBlock'
import { useTaskProgressMessages } from '~/hooks/task/useTaskProgressMessages'
import { useCompactViewport } from '~/hooks/useCompactViewport'
import { useProjectName } from '~/hooks/useTaskAware'
import {
  AgentTaskStatus,
  agentTaskStatusFromJSON,
  CommandType,
  commandTypeFromJSON,
  Diff,
  MessageType,
  messageTypeFromJSON,
  MigrationDocumentOutput,
  TaskStatusAndRequestResponseTuple
} from '~/protos/agents'
import { bridge } from '~/services/bridge'
import { applyDiff } from '~/services/taskService'
import { TabOption } from '~/types/TabOption'
import {
  getDocumentListProps,
  isDocumentGeneratingCommand,
  isTaskEffectivelyDone
} from '~/task/commandTypeUtils'
import { fetchWrapper } from '~/utils/fetchWrapper'
import { AppEvents, extractApiErrorMessage, globalEventEmitter } from '~/utils/shared'
import { BetterToast } from '~/utils/toast'
import { CreditLimitExceededMessage } from '../common/CreditLimitExceededMessage'
import { DocumentList } from '../documents/DocumentList'
import CodeEditor from '../editors/CodeEditor'
import DocMarkdown from '../documents/DocMarkdown'
import {
  DocsPanel,
  DownloadDocumentDropdown,
  DownloadDropdownPlaceholder,
  preprocessContent
} from './DocsPanel'
import ReviewBrief from './ReviewBrief'
import TaskProgressMessageView from './TaskProgressMessageView'

// ViewType, the command→profile classifier, the tab-visibility table, and the
// diff-status taxonomy all live in the bundled tab contract (`~/task/taskTabs`).
export type { ViewType } from '~/task/taskTabs'

// ─── Constants ───────────────────────────────────────────────────────────────

const TAB_CONTENT_CLASS = 'flex-1 flex flex-col min-h-0 overflow-auto'

// ─── Utility Functions ───────────────────────────────────────────────────────

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  const chunkSize = 0x8000
  let binary = ''
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize))
  }
  return btoa(binary)
}

function extractDiffsFromTuple(tuple: TaskStatusAndRequestResponseTuple): Diff[] | null {
  const typedTuple = tuple as {
    taskStatus: AgentTaskStatus
    request?: { diff?: Diff[] }
  } & TaskStatusAndRequestResponseTuple
  const responseContent = typedTuple.response?.[0]?.responseContentStream
  const taskStatus = agentTaskStatusFromJSON(typedTuple.taskStatus)

  if (MIGRATION_DIFF_STATUSES.has(taskStatus)) {
    return responseContent?.migrationDocOutput?.diffs || null
  }
  return responseContent?.diff || typedTuple.request?.diff || null
}

function isDiffBearingTuple(tuple: TaskStatusAndRequestResponseTuple): boolean {
  const status = agentTaskStatusFromJSON((tuple as { taskStatus: AgentTaskStatus }).taskStatus)
  return DIFF_BEARING_STATUSES.has(status)
}

/** Search target tuple first, then fall back to scanning all tuples. */
function findDiffs(
  targetTuple: TaskStatusAndRequestResponseTuple | null,
  tuples: TaskStatusAndRequestResponseTuple[]
): Diff[] | null {
  if (targetTuple) {
    const result = extractDiffsFromTuple(targetTuple)
    if (result?.length) return result
    if (isDiffBearingTuple(targetTuple)) return null
  }
  for (let i = tuples.length - 1; i >= 0; i -= 1) {
    const tuple = tuples[i]
    const result = extractDiffsFromTuple(tuple)
    if (result?.length) return result
  }
  return null
}

function extractPrLinkFromTuple(tuple: TaskStatusAndRequestResponseTuple | null): string {
  if (!tuple?.response?.length) return ''

  for (let i = tuple.response.length - 1; i >= 0; i -= 1) {
    const stream = tuple.response[i]?.responseContentStream
    if (!stream || messageTypeFromJSON(stream.messageType) !== MessageType.PR) continue

    const prLink = stream.deltaContent?.trim()
    if (prLink) return prLink
  }

  return ''
}

function findPrLink(
  targetTuple: TaskStatusAndRequestResponseTuple | null,
  tuples: TaskStatusAndRequestResponseTuple[],
  isViewingOldTuple: boolean
): string {
  const targetPrLink = extractPrLinkFromTuple(targetTuple)
  if (targetPrLink || isViewingOldTuple) return targetPrLink

  for (let i = tuples.length - 1; i >= 0; i -= 1) {
    const prLink = extractPrLinkFromTuple(tuples[i])
    if (prLink) return prLink
  }

  return ''
}

/** Search target tuple first, then scan all tuples latest-to-oldest. */
function findDeltaContent(
  targetTuple: TaskStatusAndRequestResponseTuple | null,
  tuples: TaskStatusAndRequestResponseTuple[],
  isErrored: boolean
): string {
  if (targetTuple) {
    const content = getCodeDeltaContent(targetTuple)
    if (content) return content
  }
  for (let i = tuples.length - 1; i >= 0; i--) {
    const content = getCodeDeltaContent(tuples[i] as TaskStatusAndRequestResponseTuple)
    if (content) return content
  }
  return isErrored ? 'An unexpected error occurred. Please try again or contact support.' : ''
}

function findReviewBrief(
  targetTuple: TaskStatusAndRequestResponseTuple | null,
  tuples: TaskStatusAndRequestResponseTuple[]
): string {
  if (targetTuple) {
    const content = getCodeReviewBriefContent(targetTuple)
    if (content) return content
    if (isDiffBearingTuple(targetTuple)) return ''
  }
  for (let i = tuples.length - 1; i >= 0; i -= 1) {
    const content = getCodeReviewBriefContent(tuples[i] as TaskStatusAndRequestResponseTuple)
    if (content) return content
  }
  return ''
}

function formatPrReviewBrief(prLink: string, reviewBrief: string): string {
  const normalizedPrLink = prLink.trim()
  const normalizedReviewBrief = reviewBrief.trim()
  if (!normalizedPrLink) return normalizedReviewBrief
  if (normalizedReviewBrief.includes(normalizedPrLink)) return normalizedReviewBrief

  const prDetails = `Pull request created: [View PR](${normalizedPrLink})`
  if (!normalizedReviewBrief) return prDetails

  return `${prDetails}\n\n${normalizedReviewBrief}`
}

function hasNonEmptyText(value: string | null | undefined): value is string {
  return typeof value === 'string' && value.trim() !== ''
}

function getNonEmptyMapEntries(
  inputMap: Record<string, string> | null | undefined
): Array<[string, string]> {
  return Object.entries(inputMap ?? {})
    .filter(([key, value]) => hasNonEmptyText(key) && hasNonEmptyText(value))
    .sort(([left], [right]) => left.localeCompare(right))
}

function getNonEmptyMapEntriesInOrder(
  inputMap: Record<string, string> | null | undefined
): Array<[string, string]> {
  return Object.entries(inputMap ?? {}).filter(
    ([key, value]) => hasNonEmptyText(key) && hasNonEmptyText(value)
  )
}

function hasMigrationDocOutputContent(output: MigrationDocumentOutput | null | undefined): boolean {
  if (!output) return false

  return (
    hasNonEmptyText(output.docMd) ||
    hasNonEmptyText(output.endpointsMd) ||
    hasNonEmptyText(output.codeGenSummary) ||
    (output.apiSpec?.length ?? 0) > 0 ||
    (output.migDocs?.length ?? 0) > 0 ||
    (output.diffs?.length ?? 0) > 0 ||
    getNonEmptyMapEntries(output.migDocsMap).length > 0 ||
    getNonEmptyMapEntries(output.migDocsRiskMap).length > 0 ||
    getNonEmptyMapEntries(output.codeGenSummaryMap).length > 0
  )
}

function extractMigrationDocOutput(
  tuple: TaskStatusAndRequestResponseTuple | null
): MigrationDocumentOutput | null {
  if (!tuple?.response?.length) return null

  for (let i = tuple.response.length - 1; i >= 0; i -= 1) {
    const migrationDocOutput = tuple.response[i]?.responseContentStream?.migrationDocOutput
    if (migrationDocOutput && hasMigrationDocOutputContent(migrationDocOutput)) {
      return migrationDocOutput
    }
  }

  return null
}

function findMigrationDocOutput(
  targetTuple: TaskStatusAndRequestResponseTuple | null,
  tuples: TaskStatusAndRequestResponseTuple[]
): MigrationDocumentOutput | null {
  const targetOutput = extractMigrationDocOutput(targetTuple)
  if (targetOutput) return targetOutput

  for (let i = tuples.length - 1; i >= 0; i -= 1) {
    const migrationDocOutput = extractMigrationDocOutput(tuples[i])
    if (migrationDocOutput) return migrationDocOutput
  }

  return null
}

interface MavenResultPayload {
  summary: string
  rawOutput: string
}

function extractMavenResultFromTuple(
  tuple: TaskStatusAndRequestResponseTuple | null
): MavenResultPayload | null {
  if (!tuple?.response?.length) return null

  for (let i = tuple.response.length - 1; i >= 0; i -= 1) {
    const stream = tuple.response[i]?.responseContentStream
    const metadata = stream?.jsonMetadata
    if (!metadata) continue

    try {
      const parsed = JSON.parse(metadata) as Record<string, unknown>
      const rawOutput = typeof parsed.raw_output === 'string' ? parsed.raw_output : ''
      const summary =
        typeof parsed.summary === 'string'
          ? parsed.summary
          : typeof parsed.error_message === 'string'
            ? parsed.error_message
            : stream?.deltaContent || ''

      if (summary.trim() || rawOutput.trim()) {
        return { summary, rawOutput }
      }
    } catch {
      // Ignore malformed legacy metadata and continue with older responses.
    }
  }

  return null
}

function findMavenResult(
  targetTuple: TaskStatusAndRequestResponseTuple | null,
  tuples: TaskStatusAndRequestResponseTuple[]
): MavenResultPayload | null {
  const targetResult = extractMavenResultFromTuple(targetTuple)
  if (targetResult) return targetResult

  for (let i = tuples.length - 1; i >= 0; i -= 1) {
    const result = extractMavenResultFromTuple(tuples[i])
    if (result) return result
  }

  return null
}

// ─── Small UI Components ─────────────────────────────────────────────────────

function NotificationBadge({ variant }: { variant: 'action' | 'error' }) {
  const styles =
    variant === 'action'
      ? 'bg-[#846FE1] animate-attention-badge'
      : 'bg-tokens-status-error-1 animate-error-pop'

  return (
    <span
      className={cn(
        'flex items-center justify-center min-w-[14px] h-[14px] px-1 rounded-full text-white text-[10px] font-bold',
        styles
      )}
    >
      !
    </span>
  )
}

function SummaryContent({ content }: { content: string }) {
  const processedContent = useMemo(() => preprocessContent(content), [content])
  return (
    <div className='w-full flex-1 overflow-auto p-4'>
      <DocMarkdown text={processedContent} />
    </div>
  )
}

function MavenResultsContent({ summary, rawOutput }: { summary: string; rawOutput: string }) {
  const [showRawOutput, setShowRawOutput] = useState(false)
  const processedSummary = useMemo(() => preprocessContent(summary), [summary])
  const hasRawOutput = rawOutput.trim() !== ''

  return (
    <div className='w-full flex-1 overflow-auto p-4'>
      {processedSummary ? <DocMarkdown text={processedSummary} /> : null}
      {hasRawOutput && (
        <div className='mt-4 border-t border-tokens-neutral-border-2 pt-3'>
          <button
            type='button'
            onClick={() => setShowRawOutput(value => !value)}
            className='rounded-[4px] text-sm font-medium text-tokens-status-link-3 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-tokens-status-link-2'
          >
            {showRawOutput ? 'Hide full Maven output' : 'View full Maven output'}
          </button>
          {showRawOutput && (
            <pre
              data-testid='maven-full-output'
              className='mt-3 max-h-[520px] overflow-auto whitespace-pre-wrap break-words rounded-[8px] border border-tokens-neutral-border-2 bg-tokens-neutral-fill-fill-7 p-4 font-mono text-xs leading-5 text-tokens-neutral-text-primary'
            >
              {rawOutput}
            </pre>
          )}
        </div>
      )}
    </div>
  )
}

function ReviewBriefSummaryContent({ reviewBrief }: { reviewBrief: string }) {
  return (
    <div className='w-full flex-1 overflow-auto p-4'>
      <ReviewBrief
        summary={reviewBrief}
        sticky={false}
        className='mx-auto w-full max-w-[1059px] p-6'
        contentBounded={false}
        contentClassName='text-base'
        testId='task-review-brief-summary'
      />
    </div>
  )
}

interface CodeSummaryDocsPanelProps {
  entries: Array<[string, string]>
  fallbackSummary: string
  downloadContent: string
}

function CodeSummaryDocsPanel({
  entries,
  fallbackSummary,
  downloadContent
}: CodeSummaryDocsPanelProps) {
  const displaySummary = useMemo(
    () => downloadContent || fallbackSummary || entries.map(([, summary]) => summary).join('\n\n'),
    [downloadContent, entries, fallbackSummary]
  )

  if (!displaySummary.trim()) {
    return (
      <div className='flex flex-1 items-center justify-center text-tokens-neutral-text-tertiary'>
        <Typography fontFamily='geist' fontSize='bodyMd'>
          No code summary flows available.
        </Typography>
      </div>
    )
  }

  return (
    <div className='mx-auto flex h-full min-h-0 w-full max-w-[1059px] flex-col overflow-hidden rounded-[28px] border border-tokens-neutral-border-2 bg-tokens-neutral-bg-white'>
      <header className='flex h-[80px] shrink-0 items-center justify-between gap-4 border-b border-tokens-neutral-border-2 px-12'>
        <Typography
          fontFamily='geist'
          fontSize='titleSm'
          fontWeight='semibold'
          className='text-tokens-neutral-text-primary'
        >
          Code Generation Summary
        </Typography>
        <Suspense fallback={DownloadDropdownPlaceholder}>
          <DownloadDocumentDropdown content={downloadContent} />
        </Suspense>
      </header>

      <div className='min-h-0 flex-1 overflow-auto px-12 py-8'>
        <DocMarkdown text={preprocessContent(displaySummary)} />
      </div>
    </div>
  )
}

// DocsPanel and its supporting types/components live in ./DocsPanel.tsx —
// they're a self-contained subview that this file only mounts inside the
// 'docs' tab branch.

// ─── Hooks ───────────────────────────────────────────────────────────────────

interface TaskResultsData {
  tuples: TaskStatusAndRequestResponseTuple[]
  targetTuple: TaskStatusAndRequestResponseTuple | null
  commandType: CommandType
  taskId: number | undefined
  s3TarFilePath: string | null
  isDataWeaveCommand: boolean
  isMigrationAcceleratorCommand: boolean
  isMigrationDocCommand: boolean
  isMavenCommand: boolean
  isDocumentCommand: boolean
  taskStatus: AgentTaskStatus | undefined
  isViewingOldTuple: boolean
  effectivelyDone: boolean
  isErrored: boolean
  diffs: Diff[] | null
  hasDiffs: boolean
  prLink: string
  deltaContent: string
  reviewBrief: string
  mavenResult: MavenResultPayload | null
  mavenSummary: string
  mavenRawOutput: string
  migrationDocOutput: MigrationDocumentOutput | null
  migrationDocEntries: Array<[string, string]>
  migrationRiskEntries: Array<[string, string]>
  migrationCodeGenSummary: string
  migrationCodeGenSummaryEntries: Array<[string, string]>
  hasMigrationDocEntries: boolean
  hasMigrationRiskEntries: boolean
  hasMigrationCodeGenSummary: boolean
  documentListProps: ReturnType<typeof getDocumentListProps> | undefined
}

/** Extracts and memoizes all tuple-derived data from the current task. */
function useTaskResultsData(index?: number): TaskResultsData {
  const { task } = useTask()

  const tuples = useMemo(
    () => (task?.details?.orderedStateResponses ?? []) as TaskStatusAndRequestResponseTuple[],
    [task?.details?.orderedStateResponses]
  )

  const targetIndex = index !== undefined ? index : tuples.length - 1
  const targetTuple = tuples.length > 0 && targetIndex >= 0 ? tuples[targetIndex] : null

  const commandType = commandTypeFromJSON(task?.cmd_type)
  const taskId = task?.task_id
  const originalRequest = task ? getTaskRequest(task) : null
  const s3TarFilePath = originalRequest?.metadata?.codeContext?.s3TarFilePath || null
  const isDataWeaveCommand = DATAWEAVE_COMMANDS.has(commandType)
  const isMigrationAcceleratorCommand = commandType === CommandType.MIGRATION_ACCELERATOR
  const isMigrationDocCommand = commandType === CommandType.MIGRATION_DOCUMENT
  const isMavenCommand = commandType === CommandType.MVN_CMD
  const isDocumentCommand = isDocumentGeneratingCommand(commandType)

  const taskStatus = useMemo(() => {
    if (index !== undefined && targetTuple) {
      return agentTaskStatusFromJSON((targetTuple as { taskStatus: AgentTaskStatus }).taskStatus)
    }
    return task?.status ? agentTaskStatusFromJSON(task.status) : undefined
  }, [index, targetTuple, task?.status])

  const isViewingOldTuple = index !== undefined && index < tuples.length - 1
  const effectivelyDone = isTaskEffectivelyDone(taskStatus, commandType)
  const isErrored = taskStatus === AgentTaskStatus.ERRORED

  const prLink = useMemo(
    () => findPrLink(targetTuple, tuples, isViewingOldTuple),
    [targetTuple, tuples, isViewingOldTuple]
  )
  const rawDiffs = useMemo(() => findDiffs(targetTuple, tuples), [targetTuple, tuples])
  const diffs = rawDiffs

  const rawDeltaContent = useMemo(
    () => findDeltaContent(targetTuple, tuples, isErrored),
    [targetTuple, tuples, isErrored]
  )
  const rawReviewBrief = useMemo(() => findReviewBrief(targetTuple, tuples), [targetTuple, tuples])
  const deltaContent = prLink ? '' : rawDeltaContent
  const reviewBrief = formatPrReviewBrief(prLink, rawReviewBrief)
  const mavenResult = useMemo(() => findMavenResult(targetTuple, tuples), [targetTuple, tuples])
  const mavenSummary = mavenResult?.summary || (isMavenCommand ? deltaContent : '')
  const mavenRawOutput = mavenResult?.rawOutput || ''
  const migrationDocOutput = useMemo(
    () => findMigrationDocOutput(targetTuple, tuples),
    [targetTuple, tuples]
  )
  // ── LEGACY MIGRATION DOC FALLBACK CONTRACT ──────────────────────────────
  //
  // Full write-up: ./LEGACY_DOCS_WARNING.md (co-located in this dir).
  //
  // `migDocsMap` / `migDocsRiskMap` are the per-endpoint breakdown introduced
  // alongside the merged Docs tab + DocsPanel. Older `MigrationDocumentOutput`
  // payloads (pre-breakdown agent runs that are still in the DB) only carry
  // `docMd` / `endpointsMd` / `migDocs[]` and have no maps at all.
  //
  // For those legacy payloads:
  //   - Both arrays below collapse to `[]`.
  //   - The Docs tab self-gates itself out in `useTabManagement` because
  //     `availableTabs` requires `hasMigrationDocEntries || hasMigrationRiskEntries`.
  //   - The Results tab survives because its visibility predicate is
  //     `isMigrationDocCommand && deltaContent !== ''`, and `deltaContent`
  //     resolves through `getCodeDeltaContent`'s fallback chain
  //     (`docMd` → `endpointsMd` → `migDocs.join('\n\n')`).
  //   - The user reads the legacy doc as a single SummaryContent blob in the
  //     Results tab, with the Download button still wired up.
  //
  // ⚠️  Before changing ANY of (a) the `'docs'` tab gating, (b) the
  // `'results'` tab gating for migration docs, or (c) `getCodeDeltaContent`'s
  // migration-doc fallbacks, you MUST do one of:
  //
  //   1. Backfill `migDocsMap` on every old `MigrationDocumentOutput` row in
  //      the DB so the new path applies uniformly, OR
  //   2. Synthesize a single legacy entry here, e.g.
  //        if (migrationDocEntries.length === 0 && migrationDocOutput?.docMd) {
  //          migrationDocEntries = [['Documentation', migrationDocOutput.docMd]]
  //        }
  //      so the DocsPanel becomes the legacy viewer too.
  //
  // Without one of those, dropping the Results-tab fallback turns every old
  // migration task into a blank page (Docs tab hidden + Results tab hidden +
  // nothing else to fall back to).
  //
  // Regression coverage lives in `TaskResultsView.test.tsx` under the
  // `'TaskResultsView legacy migration document payloads (no per-endpoint
  // breakdown)'` describe block — keep those tests green or migrate the data.
  const migrationDocEntries = useMemo(
    () => getNonEmptyMapEntries(migrationDocOutput?.migDocsMap),
    [migrationDocOutput]
  )
  const migrationRiskEntries = useMemo(
    () => getNonEmptyMapEntries(migrationDocOutput?.migDocsRiskMap),
    [migrationDocOutput]
  )
  const migrationCodeGenSummary = useMemo(
    () =>
      hasNonEmptyText(migrationDocOutput?.codeGenSummary) ? migrationDocOutput.codeGenSummary : '',
    [migrationDocOutput]
  )
  const migrationCodeGenSummaryEntries = useMemo(
    () => getNonEmptyMapEntriesInOrder(migrationDocOutput?.codeGenSummaryMap),
    [migrationDocOutput]
  )

  const documentListProps = isDocumentCommand ? getDocumentListProps(commandType) : undefined

  return {
    tuples,
    targetTuple,
    commandType,
    taskId,
    s3TarFilePath,
    isDataWeaveCommand,
    isMigrationAcceleratorCommand,
    isMigrationDocCommand,
    isMavenCommand,
    isDocumentCommand,
    taskStatus,
    isViewingOldTuple,
    effectivelyDone,
    isErrored,
    diffs,
    hasDiffs: (diffs?.length ?? 0) > 0,
    prLink,
    deltaContent,
    reviewBrief,
    mavenResult,
    mavenSummary,
    mavenRawOutput,
    migrationDocOutput,
    migrationDocEntries,
    migrationRiskEntries,
    migrationCodeGenSummary,
    migrationCodeGenSummaryEntries,
    hasMigrationDocEntries: migrationDocEntries.length > 0,
    hasMigrationRiskEntries: migrationRiskEntries.length > 0,
    hasMigrationCodeGenSummary:
      migrationCodeGenSummary !== '' || migrationCodeGenSummaryEntries.length > 0,
    documentListProps
  }
}

interface TabConfig {
  value: ViewType
  label: string
  badge?: 'action' | 'error'
  fixedWidth?: string
}

// ─── Tab selection persistence ───────────────────────────────────────────────
//
// Selected tab is persisted to `sessionStorage` keyed by task id so that
// switching to another task and back restores the user's last-selected tab
// (and so does a same-tab reload). Storage is intentionally session-scoped:
// closing the tab / browser discards the selection — fresh sessions start
// from each task's `defaultView`.
//
// `sessionStorage` access is wrapped in try/catch because private browsing
// modes and quota-exceeded states throw synchronously. Failures silently
// fall back to in-memory state, so persistence is best-effort, not load-
// bearing.

const TAB_STORAGE_KEY_PREFIX = 'curie:task-tab:'

function readStoredTab(taskId: number | undefined): ViewType | null {
  if (taskId === undefined) return null
  try {
    const raw = sessionStorage.getItem(`${TAB_STORAGE_KEY_PREFIX}${taskId}`)
    return (raw as ViewType | null) ?? null
  } catch {
    return null
  }
}

function writeStoredTab(taskId: number | undefined, tab: ViewType): void {
  if (taskId === undefined) return
  try {
    sessionStorage.setItem(`${TAB_STORAGE_KEY_PREFIX}${taskId}`, tab)
  } catch {
    // Private mode / quota exceeded — in-memory state still works, so
    // persistence just degrades silently.
  }
}

/** Manages tab configuration, visibility, active view, and badge state. */
function useTabManagement(
  showAgentProgress: boolean,
  data: Pick<
    TaskResultsData,
    | 'hasDiffs'
    | 'deltaContent'
    | 'reviewBrief'
    | 'mavenSummary'
    | 'mavenRawOutput'
    | 'isErrored'
    | 'taskStatus'
    | 'commandType'
    | 'isViewingOldTuple'
    | 'hasMigrationDocEntries'
    | 'hasMigrationRiskEntries'
    | 'hasMigrationCodeGenSummary'
    | 'taskId'
  >,
  isApproving: boolean
) {
  const {
    hasDiffs,
    deltaContent,
    reviewBrief,
    mavenSummary,
    mavenRawOutput,
    isErrored,
    taskStatus,
    commandType,
    isViewingOldTuple,
    hasMigrationDocEntries,
    hasMigrationRiskEntries,
    hasMigrationCodeGenSummary,
    taskId
  } = data

  // Which task is this? — the single place the command dimension enters tab
  // selection (was a cross-product of isDataWeave/isMigrationDoc/... booleans).
  const profile = getTaskTabProfile(commandType)

  const hasSummaryContent = deltaContent !== '' || reviewBrief !== ''
  const hasMavenResultContent = mavenSummary.trim() !== '' || mavenRawOutput.trim() !== ''

  const contentFlags = useMemo<TabContentFlags>(
    () => ({
      hasDiffs,
      hasSummaryContent,
      hasMavenResultContent,
      hasDeltaContent: deltaContent !== '',
      hasMigrationDocEntries,
      hasMigrationRiskEntries,
      hasMigrationCodeGenSummary
    }),
    [
      hasDiffs,
      hasSummaryContent,
      hasMavenResultContent,
      deltaContent,
      hasMigrationDocEntries,
      hasMigrationRiskEntries,
      hasMigrationCodeGenSummary
    ]
  )

  // The visible tabs, in order. The full visibility contract — which tabs each
  // task family shows and under what content condition — is the TAB_RULES table
  // in `~/task/taskTabs`. (The migration 'results'/'docs' predicates there are
  // the LEGACY MIGRATION DOC FALLBACK — read that contract in `useTaskResultsData`
  // before changing them.)
  const availableTabs = useMemo(
    () => getVisibleTabs(profile, contentFlags, showAgentProgress),
    [profile, contentFlags, showAgentProgress]
  )

  const needsCodeReview = useMemo(() => {
    if (isViewingOldTuple || isApproving || !hasDiffs || !taskStatus) return false
    return TaskStatuses[taskStatus]?.group === 'NEED_ACTION'
  }, [hasDiffs, taskStatus, isViewingOldTuple, isApproving])

  const showActionBadge = needsCodeReview && !isTaskEffectivelyDone(taskStatus, commandType)

  const tabConfigs = useMemo<TabConfig[]>(() => {
    return availableTabs.map((tab): TabConfig => {
      switch (tab) {
        case 'agent-progress':
          return { value: tab, label: 'Agent Progress' }
        case 'review-code':
          return {
            value: tab,
            label: 'Review Code',
            badge: showActionBadge ? 'action' : undefined,
            fixedWidth: showActionBadge ? 'w-[150px]' : undefined
          }
        case 'summary':
          return {
            value: tab,
            label: 'Summary',
            badge: isErrored ? 'error' : undefined,
            fixedWidth: isErrored ? 'w-[120px]' : undefined
          }
        case 'results':
          return {
            value: tab,
            label: 'Results',
            badge: showActionBadge ? 'action' : undefined,
            fixedWidth: showActionBadge ? 'w-[120px]' : undefined
          }
        case 'docs':
          return { value: tab, label: 'Docs' }
        case 'code-generation-summary':
          return { value: tab, label: 'Code Summary' }
      }
    })
  }, [availableTabs, showActionBadge, isErrored])

  const defaultView = useMemo(() => {
    if (showAgentProgress && availableTabs.includes('agent-progress')) return 'agent-progress'
    if (availableTabs.includes('review-code')) return 'review-code'
    return availableTabs[0] || 'results'
  }, [availableTabs, showAgentProgress])

  // Initial value: honour the stored tab for this task if it's still a
  // valid option, otherwise fall back to the computed defaultView.
  const [viewType, setViewTypeState] = useState<ViewType>(() => {
    const stored = readStoredTab(taskId)
    if (stored && availableTabs.includes(stored)) return stored
    return defaultView
  })

  // Task switches re-use the same TaskResultsView instance (TaskDisplay
  // doesn't key it by task_id), so when `taskId` changes we need to
  // re-read storage for the new task. We do this with the React-recommended
  // "adjusting state during render" pattern (setState-in-render gated on a
  // prop comparison) rather than a useEffect. The effect version caused a
  // visible double-render on every task switch: render 1 would mount the
  // previous task's viewType, the effect would then setState to the new
  // task's stored viewType, and render 2 would mount again — which in turn
  // double-fired the expensive wrapper remount + fade-slide animation on
  // heavy tabs. When setState is called during render React discards the
  // in-progress render and immediately re-runs the component, so the JSX
  // that actually commits already uses the corrected viewType.
  const [lastSyncedTaskId, setLastSyncedTaskId] = useState(taskId)
  if (lastSyncedTaskId !== taskId) {
    setLastSyncedTaskId(taskId)
    const stored = readStoredTab(taskId)
    setViewTypeState(stored && availableTabs.includes(stored) ? stored : defaultView)
  }

  // If the current tab disappears from availableTabs (rare, e.g. streaming
  // data changes what's visible mid-task), snap to defaultView. We do NOT
  // persist this fallback — if the user explicitly picked the tab earlier,
  // storage should still reflect that and pick it up on the next visit when
  // the tab reappears.
  useEffect(() => {
    if (!availableTabs.includes(viewType)) setViewTypeState(defaultView)
  }, [availableTabs, viewType, defaultView])

  // Public setter: also persist to sessionStorage so the choice survives
  // task switches and reloads within the same browser tab. Writes are
  // scoped to the user's explicit action, not to automatic fallbacks, to
  // avoid clobbering a good stored value with a transient one during task
  // switches.
  const setViewType = useCallback(
    (tab: ViewType) => {
      setViewTypeState(tab)
      writeStoredTab(taskId, tab)
    },
    [taskId]
  )

  const activeView = availableTabs.includes(viewType) ? viewType : defaultView

  return { tabConfigs, activeView, setViewType, needsCodeReview }
}

/** Handles downloading the original uploaded repo via bridge or browser. */
function useDownloadOriginalRepo(taskId: number | undefined) {
  return useCallback(async () => {
    if (!taskId) {
      BetterToast.error('Task ID not available')
      return
    }

    try {
      const response = await fetchWrapper(`/api/task/${taskId}/download-original-repo`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(extractApiErrorMessage(error, 'Failed to get download URL'))
      }

      const { url, filename } = await response.json()
      const resolvedFilename = filename || 'original-repo.tar.gz'

      if (bridge.isInPlugin()) {
        const downloadResponse = await fetch(url)
        if (!downloadResponse.ok) throw new Error('Failed to download repository archive')
        const base64 = arrayBufferToBase64(await downloadResponse.arrayBuffer())

        const result = await bridge.saveFile(base64, resolvedFilename)
        if (result.success) {
          BetterToast.success(`Saved to ${result.data?.path || resolvedFilename}`)
          return
        }
        if (result.error === 'Save cancelled by user') return

        if (result.error === 'unsupported') {
          const workspaceInfo = await bridge.getWorkspaceInfo()
          const rootPath = workspaceInfo.data?.rootPath
          if (!rootPath) throw new Error('No workspace path available for download')
          const downloadDir = `${rootPath}/Downloaded`
          await bridge.call('fs', 'mkdir', { path: downloadDir })
          const writeResult = await bridge.call('fs', 'writeBinary', {
            path: `${downloadDir}/${resolvedFilename}`,
            content: base64
          })
          if (!writeResult.success) throw new Error(writeResult.error || 'Failed to save file')
          BetterToast.success(`Saved to Downloaded/${resolvedFilename}`)
          return
        }

        throw new Error(result.error || 'Failed to save file')
      }

      const a = document.createElement('a')
      a.href = url
      a.download = resolvedFilename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      BetterToast.success('Download started!')
    } catch (error) {
      console.error('Error downloading original repo:', error)
      BetterToast.error(
        error instanceof Error ? error.message : 'Failed to download original repository'
      )
    }
  }, [taskId])
}

// ─── Main Component ──────────────────────────────────────────────────────────

interface TaskResultsViewProps {
  readOnly?: boolean
  /** Optional tuple index — uses that specific tuple; otherwise uses latest */
  index?: number
  /** Show the Agent Progress tab. Set false for modals/detail views. */
  showAgentProgress?: boolean
}

function TaskResultsView({
  readOnly: readOnlyProp = false,
  index,
  showAgentProgress = true
}: TaskResultsViewProps): JSX.Element {
  const isCompactViewport = useCompactViewport()
  const { isResultVisible } = useTask()
  const { workspaceUuid, uuid } = useParams<{ workspaceUuid: string; uuid: string }>()
  const conversationUuid = useConversationUuid()
  const {
    isEditable,
    showApproveButton: canApprove,
    showFeedbackControls: canFeedback
  } = useCodeEditorVisibility()
  const taskBlock = useTaskBlock()
  const projectName = useProjectName()

  const readOnly = readOnlyProp || !isEditable
  const data = useTaskResultsData(index)
  const {
    tuples,
    targetTuple,
    commandType,
    taskId,
    s3TarFilePath,
    isMavenCommand,
    isDocumentCommand,
    isMigrationDocCommand,
    isMigrationAcceleratorCommand,
    taskStatus,
    isViewingOldTuple,
    effectivelyDone,
    diffs,
    hasDiffs,
    prLink,
    deltaContent,
    reviewBrief,
    mavenSummary,
    mavenRawOutput,
    migrationCodeGenSummary,
    migrationCodeGenSummaryEntries,
    migrationDocEntries,
    migrationRiskEntries,
    hasMigrationDocEntries,
    hasMigrationRiskEntries,
    documentListProps
  } = data

  const { data: progressMessages = [], isLoading: progressMessagesLoading } =
    useTaskProgressMessages(conversationUuid, taskId ? String(taskId) : '', {
      enabled: showAgentProgress && !!conversationUuid && !!taskId
    })

  const [isApproving, setIsApproving] = useState(false)

  const { tabConfigs, activeView, setViewType, needsCodeReview } = useTabManagement(
    showAgentProgress,
    data,
    isApproving
  )
  const codeSummaryDownloadContent =
    migrationCodeGenSummary ||
    migrationCodeGenSummaryEntries.map(([, summary]) => summary).join('\n\n')

  const showApproveButton = canApprove && !isViewingOldTuple && !effectivelyDone
  const showFeedbackControls =
    canFeedback && !isViewingOldTuple && taskStatus === AgentTaskStatus.DIFF_GENERATED
  const downloadTupleIndex = index !== undefined && targetTuple ? index : undefined

  const handleDownloadOriginalRepo = useDownloadOriginalRepo(taskId)

  const handleApproveResults = useCallback(async () => {
    if (!workspaceUuid || !uuid || !taskId || !diffs || isApproving) return
    setIsApproving(true)
    setViewType('agent-progress')
    try {
      await applyDiff({ workspaceUuid, uuid, taskId, diffs, commandType })
      BetterToast.success('Results approved')
      globalEventEmitter.emit(AppEvents.setActiveTab, TabOption.HELP)
    } catch (error) {
      console.error('Error approving results:', error)
      BetterToast.error('Failed to approve results')
    } finally {
      setIsApproving(false)
    }
  }, [workspaceUuid, uuid, taskId, diffs, commandType, isApproving, setViewType])

  // ── Guard / loading states ──

  const hasProgressMessages = progressMessages.length > 0
  const hasTuples = tuples.length > 0
  const hasAnyContent = !!taskBlock || hasTuples || deltaContent !== '' || prLink !== ''
  const isDecidingProgress =
    showAgentProgress && progressMessagesLoading && !hasProgressMessages && !hasAnyContent

  if (!isResultVisible) return <CreditLimitExceededMessage />
  if (isDecidingProgress) return <LoadingState message='Loading task...' className='h-full' />

  // ── Render ──

  return (
    <div className='flex flex-col items-center w-full h-full min-h-0 bg-[#fbfbfb] dark:bg-tokens-neutral-bg-white'>
      <SegmentControls
        defaultValue={activeView}
        value={activeView}
        onValueChange={key => setViewType(key as ViewType)}
        className={cn('flex flex-col w-full h-full min-h-0', isCompactViewport ? 'gap-3' : 'gap-6')}
      >
        {tabConfigs.length > 1 && (
          <div className={cn('flex w-full shrink-0', isCompactViewport ? 'px-1 py-1' : 'px-6')}>
            <div className='flex justify-start w-full overflow-x-auto'>
              <SegmentControlsList>
                {tabConfigs.map(tab => (
                  <SegmentControlsTrigger
                    key={tab.value}
                    value={tab.value}
                    className={tab.fixedWidth}
                  >
                    {tab.badge ? (
                      <span className='flex items-center gap-3'>
                        {tab.label}
                        <NotificationBadge variant={tab.badge} />
                      </span>
                    ) : (
                      tab.label
                    )}
                  </SegmentControlsTrigger>
                ))}
              </SegmentControlsList>
            </div>
          </div>
        )}

        {/* Keyed on `${taskId}-${activeView}` so the entrance animation runs
            both on task change AND on every tab switch. Each tab's content
            already unmounts via its own `activeView === '…'` conditional
            below, so re-keying the wrapper costs no additional mount work —
            it just restarts the fade-slide-in CSS animation, giving every
            tab the same intro the agent-progress one had. */}
        <div
          key={`${taskId}-${activeView}`}
          // Mirrored as a data attribute so a regression test can assert both
          // (a) the animation class stays put on every tab and (b) the key
          // includes activeView so the wrapper actually remounts on tab
          // switch (which is what restarts the CSS animation).
          data-testid='tab-content-wrapper'
          data-fade-slide-key={`${taskId}-${activeView}`}
          className='flex-1 flex flex-col w-full min-h-0 animate-fade-slide-in overflow-hidden'
        >
          {activeView === 'agent-progress' && (
            <SegmentControlsContent value='agent-progress' className={TAB_CONTENT_CLASS}>
              {/* Branch order intentionally prioritises rendering over loading
                  state: if we already have tuples or progress messages AND a
                  taskStatus we can draw the timeline, even if the progress-
                  messages fetch is still resolving in the background. This
                  avoids a visual "wheel jump" during task open — previously
                  the outer `isDecidingProgress` guard would drop a big
                  centered spinner and this branch would immediately replace
                  it with a 128-px top-aligned spinner, reading as two
                  different wheels in two different positions back-to-back.
                  The remaining spinner now fills the tab area so the rare
                  fall-through case (taskBlock / deltaContent present but no
                  tuples/messages yet) stays in the same vertical position
                  the outer wheel occupied. */}
              {(hasProgressMessages || hasTuples) && taskStatus ? (
                <div
                  className={cn(
                    'flex flex-col mx-auto flex-1',
                    isCompactViewport ? 'w-full' : 'w-[900px]'
                  )}
                >
                  <TaskProgressMessageView
                    progressMessages={progressMessages}
                    taskStatus={taskStatus}
                    tuples={tuples}
                    timelineBlocks={
                      (taskBlock?.children ?? []) as Array<{
                        id?: string
                        fields?: Record<string, unknown>
                        children?: unknown[] | null
                      }>
                    }
                    onNavigateToReviewCode={
                      hasDiffs
                        ? () => {
                            // Migration Accelerator is a document command but surfaces diffs under
                            // the 'review-code' tab (no 'results' tab exists for it).
                            const target: ViewType = isMigrationAcceleratorCommand
                              ? 'review-code'
                              : isDocumentCommand
                                ? 'results'
                                : 'review-code'
                            setViewType(target)
                          }
                        : undefined
                    }
                    onNavigateToDocument={(() => {
                      // Pick whichever doc-viewer tab actually exists for this
                      // command. Skip the redirect (and let the modal open) if
                      // no suitable tab is available.
                      const hasMergedDocs = hasMigrationDocEntries || hasMigrationRiskEntries
                      if (hasMergedDocs) return () => setViewType('docs')
                      // Non-migration document commands (e.g. Integration Test)
                      // surface generated docs under the 'results' tab via
                      // DocumentList. Migration Accelerator never has a
                      // 'results' tab, so we skip the redirect there.
                      if (
                        isDocumentCommand &&
                        hasDiffs &&
                        !isMigrationAcceleratorCommand &&
                        !isMigrationDocCommand
                      ) {
                        return () => setViewType('results')
                      }
                      return undefined
                    })()}
                    isDocumentCommand={isDocumentCommand}
                    commandType={commandType}
                  />
                </div>
              ) : progressMessagesLoading ? (
                // Fall-through: we have *some* content (taskBlock or
                // deltaContent) that let us past the outer isDecidingProgress
                // guard, but not enough to draw the timeline yet and the
                // progress-messages fetch is still running. Fill the tab
                // area so the spinner stays roughly where the outer one
                // was instead of snapping to the top of the panel.
                <LoadingState message='Loading progress...' className='flex-1 min-h-0' />
              ) : !hasProgressMessages && !hasTuples ? (
                <div className='flex items-center justify-center flex-1 min-h-0 text-gray-500'>
                  <p>No progress messages yet</p>
                </div>
              ) : null}
            </SegmentControlsContent>
          )}

          {activeView === 'review-code' && (
            <SegmentControlsContent value='review-code' className={TAB_CONTENT_CLASS}>
              <CodeEditor
                diffs={diffs}
                readOnly={readOnly}
                showApproveButton={showApproveButton}
                showFeedbackControls={showFeedbackControls}
                commandType={commandType}
                taskId={taskId}
                taskStatus={taskStatus}
                tupleIndex={downloadTupleIndex}
                onDownloadOriginalRepo={s3TarFilePath ? handleDownloadOriginalRepo : undefined}
                isApproving={isApproving}
                onApprovingChange={setIsApproving}
                onApproveStart={() => setViewType('agent-progress')}
                projectName={projectName}
                prLink={prLink}
              />
            </SegmentControlsContent>
          )}

          {activeView === 'summary' && (
            <SegmentControlsContent value='summary' className={TAB_CONTENT_CLASS}>
              {reviewBrief ? (
                <ReviewBriefSummaryContent reviewBrief={reviewBrief} />
              ) : (
                <SummaryContent content={deltaContent} />
              )}
            </SegmentControlsContent>
          )}

          {activeView === 'results' && (
            <SegmentControlsContent value='results' className={TAB_CONTENT_CLASS}>
              {hasDiffs && diffs ? (
                <DocumentList
                  diffs={diffs}
                  {...(documentListProps || { title: 'Generated Documents', description: '' })}
                  onApprove={
                    showApproveButton && needsCodeReview && !isApproving
                      ? handleApproveResults
                      : undefined
                  }
                />
              ) : isMavenCommand ? (
                <MavenResultsContent summary={mavenSummary} rawOutput={mavenRawOutput} />
              ) : (
                <div className='flex flex-col flex-1 overflow-hidden'>
                  {deltaContent && isMigrationDocCommand && (
                    <div className='flex justify-end px-4 pt-2'>
                      <Suspense fallback={DownloadDropdownPlaceholder}>
                        <DownloadDocumentDropdown content={deltaContent} />
                      </Suspense>
                    </div>
                  )}
                  <SummaryContent content={deltaContent} />
                </div>
              )}
            </SegmentControlsContent>
          )}

          {activeView === 'docs' && (
            <SegmentControlsContent value='docs' className={TAB_CONTENT_CLASS}>
              <DocsPanel
                docEntries={migrationDocEntries}
                riskEntries={migrationRiskEntries}
                isCompactViewport={isCompactViewport}
                taskId={taskId}
              />
            </SegmentControlsContent>
          )}

          {activeView === 'code-generation-summary' && (
            <SegmentControlsContent value='code-generation-summary' className={TAB_CONTENT_CLASS}>
              <CodeSummaryDocsPanel
                entries={migrationCodeGenSummaryEntries}
                fallbackSummary={migrationCodeGenSummary}
                downloadContent={codeSummaryDownloadContent}
              />
            </SegmentControlsContent>
          )}
        </div>
      </SegmentControls>
    </div>
  )
}

export default TaskResultsView
